import React, { useState, useEffect, ReactElement } from "react";
import { BrowserView, MobileView } from "react-device-detect";
import { Button, Col, Row, Space, Collapse, Divider, Modal, Tag, Checkbox } from "antd";
import { useNavigate } from "react-router-dom";
import { CalendarOutlined, CreditCardOutlined } from "@ant-design/icons";
import { NumericFormat } from "react-number-format";
import { useAsyncAxios, utilAxiosWithAppToken } from "../../utils/customAxios";
import { additionalInfoType, modalType } from "../../pages/rent/ReservationDetails";
import { v4 as uuidv4 } from "uuid";

import * as Common from "../../commons/common";
import * as String from "../../commons/string";
import * as TypeDTO from "../../commons/typeDTO";
import * as Utils from "../../utils/utils";
import * as Request from "../../commons/request";

import Title from "./Title";
import ReservationDateInfo from "./ReservationDateInfo";
import ReservationDetail from "./detail/ReservationDetail";
import RentModal from "./RentModal";
import MobileDrawer from "./MobileDrawer";
import IconRight from "../../assets/images/home/rent/icon_right.png";
import IconError from "../../assets/images/home/rent/icon_alert_error.png";
import moment from "moment";
import CustomSheet from "../CustomSheet";
import AdditionalDriverInfo from "./AdditionalDriverInfo";
import Info from "./Info";
import VideoPlayer from "./VideoPlayer";
import styles from "./ReservationCard.module.css";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
dayjs.extend(isSameOrBefore);

type summary = {
    summaryNotice: string;
    sections: { title: string; items: { text?: any; highlight?: boolean; subItems?: string[]; content?: ReactElement<any, any> }[] }[];
};

const ReservationCard = ({
    rent,
    isLast,
    isPast,
    onCancel,
    onComplete,
}: {
    rent: TypeDTO.RentDto;
    isLast: boolean;
    isPast?: boolean;
    onCancel?: (cancelAmount: number) => void;
    onComplete?: (type?: modalType, additionalInfo?: additionalInfoType) => void;
}) => {
    const { Panel } = Collapse;
    const navigate = useNavigate();
    const [visible, setVisible] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [insurancePrice, setInsurancePrice] = useState<number>(0);
    const [rentPeakSeasons, setRentPeakSeasons] = useState<Array<TypeDTO.RentPeakSeasonDto>>();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [modalType, setModalType] = useState<modalType>("checkedUsagePolicy");
    const [paidItems, setPaidItems] = useState<Array<TypeDTO.RentPaidItemDto>>();
    const [paidInsurance, setPaidInsurance] = useState<TypeDTO.RentInsuranceDto>();
    const [checkedMap, setCheckedMap] = useState<{ [key: number]: boolean }>({});
    const [playerKeys, setPlayerKeys] = useState<string[]>([]);

    const videoContents = [
        { category: "무인 출차·반납 안내", url: "9yzAi0yHNeg" },
        { category: "캠핑카 사용 안내(외부)", url: "Mxzma3p00Y4" },
        { category: "캠핑카 사용 안내(내부)", url: "biSzRVRtRlo" },
    ];

    const [finished, setFinished] = useState<boolean[]>(videoContents.map(() => false));

    const title = {
        disabledImage: true,
        title: {
            content: rent?.rentCar.title,
        },
        description: {
            content: rent?.rentCar.description,
            style: {
                textOverflow: "ellipsis",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
            },
        },
    };

    const options = [
        {
            category: "시트",
            items: [
                { name: "운전석 의자(스위블)", price: 1000000 },
                { name: "2열 쇼파(2인승)", price: 1000000 },
                { name: "2열 쇼파(1인승)", price: 500000 },
            ],
        },
        {
            category: "싱크대",
            items: [
                { name: "싱크대 연장 테이블", price: 200000 },
                { name: "수전", price: 300000 },
                { name: "컵샤워기", price: 150000 },
                { name: "세제통", price: 100000 },
                { name: "싱크볼 파손", price: 300000 },
                { name: "도마", price: 50000 },
                { name: "타공판", price: 100000 },
            ],
        },
        {
            category: "샤워실",
            items: [
                { name: "샤워기 파손", price: 200000 },
                { name: "샤워기 거치대 파손", price: 50000 },
                { name: "템바도어", price: 800000 },
                { name: "욕실벽면/바닥", price: 300000 },
            ],
        },
        {
            category: "인테리어",
            items: [
                { name: "상/하부장 경첩", price: 50000 },
                { name: "상/하부장 문짝", price: 1000000 },
                { name: "2열 테이블", price: 200000 },
                { name: "인테리어 가구(범위별 증액)", price: 1000000 },
                { name: "방충망/블라인드(2열)", price: 300000 },
                { name: "방충망/블라인드(3열)", price: 400000 },
                { name: "방충망/블라인드(후면)", price: 500000 },
                { name: "캠핑카 창문(2열)", price: 300000 },
                { name: "캠핑카 창문(3열)", price: 400000 },
                { name: "조명", price: 500000 },
                { name: "창문 커튼(2열)", price: 50000 },
                { name: "창문 커튼(3열)", price: 100000 },
                { name: "TV거치대", price: 100000 },
                { name: "트렁크 내부 가구 문짝(1개당)", price: 200000 },
                { name: "D5 토출구", price: 50000 },
            ],
        },
        {
            category: "설비",
            items: [
                { name: "냉장고", price: 1500000 },
                { name: "냉동고", price: 800000 },
                { name: "냉동고 트레이", price: 300000 },
                { name: "전자레인지", price: 200000 },
                { name: "에어컨(천정형)", price: 2500000 },
                { name: "맥스펜", price: 400000 },
                { name: "방충망/블라인드(3열)", price: 400000 },
                { name: "방충망/블라인드(후면)", price: 500000 },
                { name: "캠핑카 창문(2열)", price: 300000 },
                { name: "캠핑카 창문(3열)", price: 400000 },
                { name: "컨트롤 패널", price: 1000000 },
                { name: "스마트TV", price: 400000 },
                { name: "콘센트(내부)", price: 100000 },
            ],
        },
        {
            category: "어닝",
            items: [
                { name: "어닝", price: 1800000 },
                { name: "어닝 핸들", price: 100000 },
                { name: "어닝 경첩 파손", price: 100000 },
                { name: "어닝 다리(1개당)", price: 200000 },
                { name: "어닝 고정 세트(8종)", price: 70000 },
            ],
        },
        {
            category: "비품",
            items: [
                { name: "에어매트", price: 300000 },
                { name: "에어매트 전동 펌프", price: 60000 },
                { name: "에어매트 바닥요", price: 50000 },
                { name: "코펠세트", price: 120000 },
                { name: "인덕션", price: 180000 },
                { name: "인덕션 패드(개당)", price: 15000 },
                { name: "조리도구세트(6종)", price: 60000 },
                { name: "식칼 셋트(2종)", price: 30000 },
                { name: "전기파리채", price: 10000 },
                { name: "전자모기향", price: 20000 },
                { name: "빗자루+쓰레받이", price: 10000 },
                { name: "팩망치", price: 20000 },
                { name: "멀티탭", price: 10000 },
                { name: "접이식 의자", price: 30000 },
                { name: "캠핑 테이블", price: 70000 },
                { name: "캠핑 의자", price: 40000 },
                { name: "베개(솜, 방수커버, 커버)", price: 50000 },
                { name: "미니베개(솜, 방수커버, 커버)", price: 40000 },
                { name: "이불", price: 65000 },
                { name: "메인침상 매트", price: 110000 },
                { name: "메인침상 매트(방수커버)", price: 15000 },
                { name: "메인침상 매트(커버)", price: 20000 },
            ],
        },
        {
            category: "기타",
            items: [
                { name: "외부샤워기", price: 200000 },
                { name: "외부 인입(출)구 캡", price: 100000 },
                { name: "외부 인입(출)구", price: 200000 },
                { name: "바닥 장판", price: 300000 },
                { name: "트렁크 바닥 깔개", price: 30000 },
            ],
        },
    ];

    const rentalConsentSummary: summary = {
        summaryNotice:
            "본 동의서는 주요 안내 중 일부 요약하여 작성되었습니다. 귀하는 위약금 및 이용에 대한 동의를 거부할 권리가 있습니다. 단, 위약금 및 이용에 대한 동의를 거부하실 경우 서비스 이용이 불가능 합니다.",
        sections: [
            {
                title: "이용객의 의무 및 책임",
                items: [
                    { text: "이용객은 위약금 규정, 렌트 이용 안내, 운행 주의사항, 차량 매뉴얼에 대해 숙지할 의무가 있습니다." },
                    { text: "운전자는 교통 법규를 준수하며 안전운전을 할 의무가 있습니다." },
                    { text: "위약금은 현장에서 즉시 결제합니다.", highlight: true },
                    { text: "본인은 위 내용을 충분히 읽고 이해하였으며, 이에 동의합니다." },
                ],
            },
            {
                title: "차량 배터리 충전 규정",
                items: [
                    { text: "차량 반납 시 배터리는 출차 상태로 충전 후 반납하며, 부족할 경우 1% 당 600원의 충전요금이 청구됩니다." },
                    { text: "배터리는 30% 전에 충전하며, 20% 미만은 주행을 금지합니다.", highlight: true },
                    { text: "본인은 위 내용을 충분히 읽고 이해하였으며, 이에 동의합니다." },
                ],
            },
            {
                title: "위약금 규정",
                items: [
                    {
                        text: "반납 지연 시",
                        subItems: [
                            "1시간 이내 지연 시 50,000원 ",
                            "1시간 초과 지연 시 100,000원",
                            "2시간 초과 지연 시 반납일 정상가 요금 100%",
                            "다음 예약건에 영향을 미칠 경우 반납일 정상가 요금 200%",
                            "차량 반납 지연으로 인하여 다음 예약건에 영향을 미칠 경우 손해배상금이 추가로 발생됩니다.",
                        ],
                    },
                    {
                        text: "규정 위반 시",
                        subItems: [
                            "차량 내 흡연: 200,000원",
                            "차량 내 냄새가 강한 음식 조리(육류, 생선구이, 청국장 등): 200,000원",
                            "샤워실 용변: 200,000원",
                            "반려동물 동반 이용: 1박당 200,000원(단, 장애인을 위한 안내견은 예외로 합니다.)",
                            "제한인원초과: 100,000원",
                            "차량 내 쓰레기 및 오물 투기·방치: 100,000원",
                        ],
                    },
                    { text: "차량 또는 비품의 파손·분실·훼손 시, 부위와 정도에 따라 손해배상금이 청구됩니다." },
                    { text: "본인은 위 내용을 충분히 읽고 이해하였으며, 이에 동의합니다." },
                ],
            },
            {
                title: "이용 규정",
                items: [
                    { text: "경사로에서는 반드시 고임목을 사용해 주세요." },
                    { text: "차량 무게 약 4톤으로 브레이크 밀림이 있을 수 있으니, 차간 거리를 충분히 유지해 주세요." },
                    { text: "예약자 본인 또는 등록된 운전자만 운전할 수 있으며, 미등록 운전으로 인한 모든 책임은 이용자에게 있습니다." },
                    { text: "사고 발생 시 상황 및 내용 등을 회사에 즉시 통보하며, 개인이 처리하지 않습니다." },
                    { text: "긴급출동서비스 비용 발생 시 전액 이용객 부담입니다.", highlight: true },
                    { text: "본인은 위 내용을 충분히 읽고 이해하였으며, 이에 동의합니다." },
                ],
            },
            {
                title: "금지 규정 및 특별사항",
                items: [
                    {
                        text: (
                            <>
                                자갈밭, 모래사장 등 비포장도로에서는 주행 및 정차(캠핑)를 절대 삼가주세요.
                                <br />
                                <span className="text-danger">견인 비용 발생 시 전액 이용객 부담</span>입니다.(특히 전촌솔밭, 관성솔밭 등)
                            </>
                        ),
                    },
                    { text: "어닝 사용 시 반드시 타이다운(고정끈) 및 고정팩을 함께 사용해야 하며, 바람이 부는 날에는 어닝 사용을 금지합니다." },
                    {
                        text: (
                            <>
                                차량 높이 제한 위반, 사용 부주의 또는 바람으로 인해 상부 설비가 파손될 경우 보험 처리가 불가하며, 해당 금액은 이용자가{" "}
                                <span className="text-danger">즉시 배상</span>해야 합니다.
                                <br />
                                - 어닝: 1,800,000원
                                <br />
                                - 에어컨(실외기): 2,500,000원
                                <br />- 맥스펜(실외기): 400,000원
                            </>
                        ),
                    },
                    { text: "본인은 위 내용을 충분히 읽고 이해하였으며, 이에 동의합니다." },
                ],
            },
            {
                title: "상세 위약금 안내",
                items: [
                    {
                        content: (
                            <div style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                                {options.map((option) => (
                                    <div key={option.category} className="mb-2">
                                        <span className="text-bold">{option.category}</span>
                                        {option.items.map((item) => (
                                            <Row justify="space-between" key={item.name}>
                                                <Col span={12}>
                                                    <span>- {item.name}</span>
                                                </Col>
                                                <Col span={12}>
                                                    <NumericFormat
                                                        style={{ float: "right" }}
                                                        value={item.price}
                                                        thousandSeparator={true}
                                                        displayType="text"
                                                        suffix="원"
                                                    />
                                                </Col>
                                            </Row>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        ),
                    },
                ],
            },
        ],
    };

    useEffect(() => {
        requestGetRentPeakSeasons();
    }, [rent]);

    useEffect(() => {
        if (isModalOpen) {
            if (modalType === "checkedUsagePolicy") {
                if (rent.checkedUsagePolicy) {
                    setCheckedMap(rentalConsentSummary.sections.map(() => true));
                } else {
                    setCheckedMap([]);
                }
            } else if (modalType === "checkedExitPolicy") {
                setPlayerKeys(videoContents?.map(() => uuidv4()));
                setFinished(videoContents?.map(() => false));

                if (rent.checkedExitPolicy) {
                    setCheckedMap(videoContents.map(() => true));
                } else {
                    setCheckedMap([]);
                }
            }
        }
    }, [isModalOpen]);

    useEffect(() => {
        if (isOpen) {
            checkDriverLicenseOpenPopup();
        }
    }, [isOpen]);

    useEffect(() => {
        if (rent) {
            const rentPayment = rent?.rentPayments?.find((payment) => payment?.rentPaymentType === "RENT");
            const insurancePayment = rent?.rentPayments?.find((payment) => payment?.rentPaymentType === "INSURANCE");

            if (insurancePayment || rentPayment?.amount === 0) {
                if (insurancePayment?.paidDate) {
                    setInsurancePrice(insurancePayment?.amount);
                }
            } else if (rent?.startDate && rent?.endDate) {
                const startDate = new Date(rent.startDate);
                const endDate = new Date(rent.endDate);
                const diffTime = Math.abs(startDate.getTime() - endDate.getTime());
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                setInsurancePrice((rent?.rentInsurance?.price || 0) * diffDays);
            }

            // 현장 결제된 유상대여품목
            setPaidItems(
                rent.rentPaidItems.filter((paidItem) =>
                    rent.rentPayments.some(
                        (payment) => payment.rentPaymentType === "PAID_ITEM" && payment.rentPaymentItem.rentPaymentItemName === paidItem.rentPaidItemName
                    )
                )
            );

            setPaidInsurance(
                rent.rentPayments.find(
                    (payment) =>
                        payment.rentPaymentType === "INSURANCE" &&
                        payment.rentPaymentItem?.rentPaymentCategory?.rentPaymentCategoryName === rent?.rentInsurance?.insuranceName
                ) && rent.rentInsurance
            );
        }
    }, [rent]);

    const getPayment = () => {
        return rent?.rentPayments?.find((payment) => payment?.rentPaymentType === "RENT");
    };

    const getRentalPrice = () => {
        const rentPayment = getPayment();
        const ids = paidItems?.map((item) => item.rentPaidItemId);
        const items = rent.rentPaidItems.filter((item) => !ids?.includes(item.rentPaidItemId));

        const paidItemPrice =
            items?.reduce(
                (total, item) =>
                    total +
                    (rent.userCoupon?.coupon.rentPaidItemId !== item.rentPaidItemId && rent.userMultiCoupon?.coupon.rentPaidItemId !== item.rentPaidItemId
                        ? item.price
                        : 0),
                0
            ) || 0;

        return rentPayment?.paymentImp ? (Number(rentPayment?.amount) || 0) - paidItemPrice - insurancePrice : 0;
    };

    const getTotalPrice = () => {
        return getRefundPrice() + (paidItems ?? []).reduce((sum, item) => sum + (item.price ?? 0), 0);
    };

    const getRefundPrice = () => {
        return rent ? (Number(getPayment()?.amount) || 0) - getCancelAmount(rent.startDate, getRentalPrice() || 0) : 0;
    };

    const getPaymentMethod = () => {
        if (getPayment()?.paymentMethod === "ONLINE" && getPayment()?.paymentImp && getPayment()?.paymentImp.cardName) {
            const stringQuota =
                getPayment()?.paymentImp.cardType === "0"
                    ? getPayment()?.paymentImp.cardQuota === 0
                        ? "/일시불"
                        : `/${getPayment()?.paymentImp.cardQuota}개월`
                    : "/체크";
            return getPayment()?.paymentImp.cardName + stringQuota;
        } else if (getPayment()?.paymentMethod === "OFFLINE_CARD" && getPayment()?.paidDate) {
            return "신용카드";
        } else if (getPayment()?.paymentMethod === "OFFLINE_BANK" && getPayment()?.paidDate) {
            return "계좌이체";
        } else if (getPayment()?.paymentMethod === "OFFLINE_CASH" && getPayment()?.paidDate) {
            return "현금";
        } else if (getPayment()?.paymentImp?.pgProvider === "kakaopay") {
            return "카카오페이";
        }
    };

    //위약금 퍼센트
    const getCancelPercent = (startDate: string) => {
        const days = moment(startDate).diff(moment().subtract(1, "day"), "days");
        let percent = 0;

        if (days >= 3) {
            if (days <= 5) {
                percent = 20;
            } else if (days <= 10) {
                percent = 50;
            } else {
                percent = 100;
            }

            return 100 - percent;
        }
        return 100;

        //return "내부 테스트 중에는 위약금 0";
    };

    //취소 금액
    const getCancelAmount = (startDate: string, rentalPrice: number) => {
        const days = moment(startDate).diff(moment().subtract(1, "day"), "days");
        let percent = 0;

        if (days >= 3) {
            if (days <= 5) {
                percent = 20;
            } else if (days <= 10) {
                percent = 50;
            } else {
                percent = 100;
            }

            return (rentalPrice / 100) * (100 - percent);
        }
        return (rentalPrice / 100) * 100;

        //return 0; //내부테스트용
    };

    //rentPeakSeason
    const requestAxiosGetRentPeakSeasons = async () => {
        const response = await utilAxiosWithAppToken().get(Request.RENT_PEAK_SEASONS_URL);
        return response.data;
    };

    const {
        loading: loadingGetRentPeakSeasons,
        error: errorGetRentPeakSeasons,
        data: resultGetRentPeakSeasons,
        execute: requestGetRentPeakSeasons,
    } = useAsyncAxios(requestAxiosGetRentPeakSeasons);

    useEffect(() => {
        if (!resultGetRentPeakSeasons) return;
        console.log("resultGetRentPeakSeasons", resultGetRentPeakSeasons);

        setRentPeakSeasons(resultGetRentPeakSeasons.rentPeakSeasons);
    }, [resultGetRentPeakSeasons]);

    useEffect(() => {
        if (!errorGetRentPeakSeasons) return;

        console.log("errorGetRentPeakSeasons", errorGetRentPeakSeasons);
    }, [errorGetRentPeakSeasons]);

    const onClick = (type: string, e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.stopPropagation();

        switch (type) {
            case "showDetail":
                navigate(Common.PAGE_RENT_DETAIL + "/" + rent?.rentCar?.rentCarId + "/" + undefined);
                break;
            case "cancel":
                setVisible(true);
                break;
            default:
                break;
        }
    };

    const handleRentCancel = () => {
        setVisible(false);
        onCancel && rent && onCancel(getRefundPrice());
    };

    const displayDetail = () => {
        return (
            <>
                <div className="space-page">
                    <Space direction="vertical" className="w-100">
                        <Space size={10}>
                            <p style={{ fontWeight: "700", color: !rent?.canceledDate ? "#005FDB" : "#E21D12" }}>{rent && Utils.convertRentType(rent)}</p>
                            <p>|</p>
                            <p style={{ color: "#192739F0" }}>
                                {getPayment()?.paidDate !== undefined && Utils.utcToLocalTime(getPayment()?.paidDate as string, Common.FORMAT_DATE_TIME)}
                            </p>
                            {isShowErrorIcon() && <img className="mr-1" src={IconError} height={16} width={16} />}
                        </Space>
                        <Title searchType={true} info={title} disabledPrice />
                        <Row gutter={10} className="w-100" style={{ marginBottom: "8px" }}>
                            <Col
                                span={rent && Utils.checkRentType(rent, [String.cancellationComplete, String.useComplete, String.inUse]) ? 24 : 12}
                                className="w-100"
                            >
                                <Button
                                    className="btn-light-border w-100"
                                    disabled={rent?.rentCar?.repairing || rent?.rentCar?.waiting || rent?.rentCar?.unavailable}
                                    onClick={(e) => onClick("showDetail", e)}
                                >
                                    {String.showGoods}
                                </Button>
                            </Col>
                            {rent && Utils.checkRentType(rent, [String.reservationComplete]) && (
                                <Col span={12}>
                                    <Button className="btn-danger w-100" onClick={(e) => onClick("cancel", e)}>
                                        {String.reservationCancel}
                                    </Button>
                                </Col>
                            )}
                        </Row>
                    </Space>
                    <Divider className="custom-divider" />
                    <AdditionalDriverInfo
                        rent={rent}
                        disabled={isPast}
                        onSuccess={(additionalDriver) => onComplete && onComplete(undefined, additionalDriver)}
                    />
                    {!isPast && (
                        <>
                            <Divider className="custom-divider" />
                            <Space direction="vertical" size="small" style={{ width: "100%" }}>
                                <h6>사전체크</h6>
                                <Space className="box" style={{ width: "100%", display: "flex", justifyContent: "space-between", padding: "5px 14px" }}>
                                    <p style={{ height: "100%" }}>위약금 및 이용 안내</p>
                                    <Button
                                        className={`${styles.btn}`}
                                        onClick={(e) => {
                                            if (isThreeDaysAgo()) {
                                                setModalType("checkedUsagePolicy");
                                                setIsModalOpen(true);
                                            } else {
                                                openCheckErrorPopup();
                                            }
                                        }}
                                    >
                                        {rent?.checkedUsagePolicy ? "완료" : "이용 전 체크"}
                                    </Button>
                                </Space>
                                <Space className="box" style={{ width: "100%", display: "flex", justifyContent: "space-between", padding: "5px 14px" }}>
                                    <p style={{ height: "100%" }}>출차 안내</p>
                                    <Button
                                        className={`${styles.btn}`}
                                        onClick={(e) => {
                                            if (isThreeDaysAgo()) {
                                                setModalType("checkedExitPolicy");
                                                setIsModalOpen(true);
                                            } else {
                                                openCheckErrorPopup();
                                            }
                                        }}
                                    >
                                        {rent?.checkedExitPolicy ? "완료" : "이용 전 체크"}
                                    </Button>
                                </Space>
                            </Space>
                        </>
                    )}
                    <Divider className="custom-divider" />
                    <h6>{String.reservationInfo}</h6>
                    <ReservationDateInfo
                        isB2b={!!rent?.rentCar?.b2b}
                        rentNo={rent?.rentNo}
                        period={
                            moment(rent?.startDate).format(Common.FORMAT_SHORT_DATE_WITH_DAY) +
                            " ~ " +
                            moment(rent?.endDate).format(Common.FORMAT_SHORT_DATE_WITH_DAY)
                        }
                    />
                    <Divider className="custom-divider" />
                    <h6>{rent && Utils.checkRentType(rent, [String.cancellationComplete]) ? "환불 금액" : "대여 금액"}</h6>
                    <ReservationDetail
                        showTitle={false}
                        value={{
                            rentCar: rent?.rentCar,
                            regionId: rent?.rentCar?.rentRegion?.rentRegionId,
                            startDate: rent?.startDate,
                            endDate: rent?.endDate,
                            extraTime: rent?.rentExtraTime,
                            rentPaidItems: rent?.rentPaidItems,
                            insurance: rent?.rentInsurance,
                            coupon: rent?.userCoupon,
                            multiCoupon: rent?.userMultiCoupon,
                            cancelAmount: getPayment()?.paymentImp?.cancelAmount,
                        }}
                        paidInsurance={paidInsurance}
                        paidItems={paidItems?.map((item) => item.rentPaidItemId)}
                        rentPeakSeasons={rentPeakSeasons}
                    />
                    <Divider />
                    <h6>{rent && Utils.checkRentType(rent, [String.cancellationComplete]) ? String.refundInfo : String.paymentInfo}</h6>
                    <Space style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                        {rent && Utils.checkRentType(rent, [String.cancellationComplete]) ? (
                            <>
                                <p>취소 일시</p>
                                <p>
                                    {getPayment()?.canceledDate !== undefined &&
                                        Utils.utcToLocalTime(getPayment()?.canceledDate as string, Common.FORMAT_DATE_TIME)}
                                </p>
                            </>
                        ) : (
                            <>
                                <p>결제 일시</p>
                                {getPayment()?.paidDate !== undefined && Utils.utcToLocalTime(getPayment()?.paidDate as string, Common.FORMAT_DATE_TIME)}
                            </>
                        )}
                    </Space>
                    <Space style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                        <p>결제 수단</p>
                        <p>{getPayment()?.paidDate !== undefined ? getPaymentMethod() : String.dash}</p>
                    </Space>
                    {!isPast && (
                        <>
                            <Divider className="custom-divider" />
                            <h6>이용 안내</h6>
                            <Info showInfo={{ show: true }} isB2b={!!rent?.rentCar?.b2b} />
                            <Divider className="custom-divider" />
                            <h6>대여 조건</h6>
                            <Info showCondition={{ show: true }} isB2b={!!rent?.rentCar?.b2b} carType={rent?.rentCar?.car?.carType?.typeName} />
                            <Divider className="custom-divider" />
                            <h6>반납 장소</h6>
                            <Info rentRegion={rent?.rentCar?.rentRegion} showMap={{ show: true }} isB2b={!!rent?.rentCar?.b2b} />
                        </>
                    )}
                </div>
                <CustomSheet
                    isOpen={isModalOpen}
                    content={
                        <div className="space-page">
                            <h6>{modalType === "checkedUsagePolicy" ? "위약금 및 이용 안내" : "출차 안내"}</h6>
                            <div className="mb-3">{modalType === "checkedUsagePolicy" ? panaltyContent() : videoContent()}</div>
                            <Button
                                style={{
                                    left: "50%",
                                    transform: "translate(-50%,0)",
                                    minHeight: "40px",
                                    borderRadius: "4px",
                                    width: "100%",
                                }}
                                disabled={
                                    modalType === "checkedUsagePolicy"
                                        ? !rentalConsentSummary.sections.every((_, index) => checkedMap[index])
                                        : !videoContents.every((_, index) => checkedMap[index])
                                }
                                onClick={handleModalOk}
                            >
                                확인
                            </Button>
                        </div>
                    }
                    snapHeight={450}
                    onClose={() => setIsModalOpen(false)}
                />
                <CustomSheet
                    isOpen={visible}
                    content={
                        rent !== undefined && (
                            <div className="space-page">
                                <h6>환불 정보</h6>
                                <p>
                                    예약을 취소하시면 환불규정에 따라 아래의 위약금이 부과됩니다. <br />
                                    계좌이체 항목은 별도 환불처리 됩니다. <br />
                                    예약을 취소하시겠습니까?
                                </p>
                                <Divider className="mt-2 mb-2" />
                                <Space style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                                    <p className="b-md">차량 대여 금액</p>
                                    <NumericFormat
                                        className="b-md"
                                        value={getRentalPrice()}
                                        thousandSeparator={true}
                                        suffix={String.priceUnit}
                                        displayType="text"
                                    />
                                </Space>
                                <Space style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                                    <p className="text-danger">{`위약금(${getCancelPercent(rent.startDate)}%)`}</p>
                                    <NumericFormat
                                        className="text-danger b-md"
                                        value={getCancelAmount(rent.startDate, getRentalPrice() || 0)}
                                        thousandSeparator={true}
                                        suffix={String.priceUnit}
                                        prefix="- "
                                        displayType="text"
                                    />
                                </Space>
                                {insurancePrice > 0 && (
                                    <Space className="mt-3" style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                                        <p className="b-md">{`자차 보험${paidInsurance ? " (계좌이체)" : ""}`}</p>
                                        <NumericFormat
                                            className="b-md"
                                            value={insurancePrice}
                                            thousandSeparator={true}
                                            suffix={String.priceUnit}
                                            displayType="text"
                                        />
                                    </Space>
                                )}
                                {rent.rentPaidItems.length > 0 && (
                                    <div className="mt-3">
                                        {rent.rentPaidItems.map((item) => (
                                            <Space key={item.rentPaidItemId} style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                                                <p className="b-md">
                                                    {item.rentPaidItemName}
                                                    {paidItems?.find((paidItem) => paidItem.rentPaidItemId === item.rentPaidItemId) && "(계좌이체)"}
                                                </p>
                                                <NumericFormat
                                                    className="b-md"
                                                    value={
                                                        rent?.userCoupon?.coupon.rentPaidItemId !== item.rentPaidItemId &&
                                                        rent?.userMultiCoupon?.coupon.rentPaidItemId !== item.rentPaidItemId
                                                            ? item.price
                                                            : 0
                                                    }
                                                    thousandSeparator={true}
                                                    suffix={String.priceUnit}
                                                    displayType="text"
                                                />
                                            </Space>
                                        ))}
                                    </div>
                                )}
                                <Space className="mt-3" style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                                    <h6>총 환불 금액</h6>
                                    <h6 className="text-price">
                                        <NumericFormat
                                            className="b-lg text-price text-bold"
                                            value={getTotalPrice()}
                                            thousandSeparator={true}
                                            suffix={String.priceUnit}
                                            displayType="text"
                                        />
                                    </h6>
                                </Space>
                                <Button
                                    style={{
                                        position: "fixed",
                                        bottom: "14px",
                                        left: "50%",
                                        transform: "translate(-50%,0)",
                                        padding: "0 1.5rem",
                                        color: "white",
                                        backgroundColor: "#e21d12",
                                        border: "1px solid #e21d12",
                                        minHeight: "40px",
                                        borderRadius: "4px",
                                        width: "90%",
                                    }}
                                    onClick={handleRentCancel}
                                >
                                    예약취소
                                </Button>
                            </div>
                        )
                    }
                    snapHeight={450}
                    onClose={() => setVisible(false)}
                />
            </>
        );
    };

    const isThreeDaysAgo = () => {
        const start = dayjs(rent.startDate).startOf("day");
        const today = dayjs().startOf("day");
        const afterThreeDays = today.add(3, "day").startOf("day");

        return start.isSameOrAfter(today) && start.isSameOrBefore(afterThreeDays);
    };

    const isShowErrorIcon = () => {
        if (isPast) {
            return false;
        }

        if (!rent?.user?.driverLicense && !rent?.additionalDriver?.driverLicense) {
            return true;
        }

        if (
            !Utils.getAvailableLiceseType.includes(rent?.user?.driverLicense?.licenseType) &&
            !Utils.getAvailableLiceseType.includes(rent?.additionalDriver?.driverLicense?.licenseType)
        ) {
            return true;
        }

        if (!rent?.checkedExitPolicy || !rent?.checkedUsagePolicy) {
            return true;
        }

        return false;
    };

    const checkDriverLicenseOpenPopup = () => {
        if (isPast) return;

        if (isThreeDaysAgo() && (!rent?.checkedExitPolicy || !rent?.checkedUsagePolicy)) {
            openCheckPopup();
        }

        if (!rent?.user?.driverLicense && !rent?.additionalDriver?.driverLicense) {
            openDriverLicensePopup();
        } else if (rent?.additionalDriver && !rent?.additionalDriver?.driverLicense) {
            openAdditionalDriverLicensePopup();
        } else if (
            !Utils.getAvailableLiceseType.includes(rent?.user?.driverLicense?.licenseType) &&
            !Utils.getAvailableLiceseType.includes(rent?.additionalDriver?.driverLicense?.licenseType) &&
            Utils.convertRentType(rent) === String.reservationComplete
        ) {
            openConfirmDriverLicensePopup();
        }
    };

    const openDriverLicensePopup = () => {
        Modal.confirm({
            title: "운전면허 미등록",
            content: (
                <p className="text-center mt-3">
                    운전면허가 등록되지 않았습니다. <br />
                    렌트 이용일 전까지 <strong>예약자</strong> 또는 <strong> 추가 운전자</strong>의 <strong>운전면허</strong>를 등록해 주세요요.
                </p>
            ),
            okText: "내 운전면허 등록",
            cancelText: "추가 운전자 등록",
            onCancel() {
                navigate(Common.PAGE_RESERVATION_DETAIL);
            },
            onOk() {
                navigate(Common.PAGE_MYPAGE);
            },
            centered: true,
            closable: true,
        });
    };

    const openAdditionalDriverLicensePopup = () => {
        Modal.info({
            title: "운전면허 미등록",
            content: (
                <p className="text-center mt-3">
                    등록한 <strong>추가 운전자</strong>의 <strong>운전면허정보</strong>가 없습니다.
                    <br />
                    추가 운전자에게 운전면허 등록을 요청해 주세요.
                </p>
            ),
            cancelText: String.confirm,
            onCancel() {},
            centered: true,
            closable: false,
        });
    };

    const openCheckErrorPopup = () => {
        Modal.info({
            title: "사전 체크",
            content: <p className="text-center mt-3">출차 안내는 렌트 이용 3일 전부터 확인하실 수 있습니다.</p>,
            cancelText: String.confirm,
            onCancel() {},
            centered: true,
            closable: false,
        });
    };

    const openConfirmDriverLicensePopup = () => {
        Modal.info({
            title: "운전면허 재확인",
            content: (
                <p className="text-center mt-3">
                    캠핑카 이용은 <strong>예약자</strong> 또는 <strong>추가 운전자</strong>의 면허가
                    <br />
                    <strong className="text-price">{`${
                        rent?.rentCar?.car?.carType?.typeName === Utils.dieselType ? "1종 보통 이상" : "1종 보통 이상, 1종 오토"
                    }`}</strong>
                    이여야 합니다.
                    <br />
                    등록한 운전면허를 다시 확인해 주세요.
                </p>
            ),
            cancelText: String.confirm,
            onCancel() {},
            centered: true,
            closable: false,
        });
    };

    const openCheckPopup = () => {
        Modal.confirm({
            title: "사전 체크",
            content: (
                <p className="text-center mt-3">
                    렌트 이용일 전까지 [예약내역] 상세보기에서
                    <br />
                    <strong>사전체크</strong>를 완료해 주세요.
                    <br />
                    사전 체크 미완료 시 차량 출차가 불가합니다.
                </p>
            ),
            okText: String.confirm,
            cancelText: "예약내역 상세보기",
            onCancel() {
                navigate(Common.PAGE_RESERVATION_DETAIL);
            },
            centered: true,
            closable: true,
        });
    };

    const toggleCheck = (index: number, checked: boolean) => {
        setCheckedMap((prev) => {
            const updated = { ...prev, [index]: checked };
            return updated;
        });
    };

    const onVideoEnd = (index: number) => {
        const updated = [...finished];
        updated[index] = true;
        setFinished(updated);
    };

    const handleModalOk = () => {
        setIsModalOpen(false);

        if ((modalType === "checkedUsagePolicy" && !rent.checkedUsagePolicy) || (modalType === "checkedExitPolicy" && !rent.checkedExitPolicy)) {
            onComplete && onComplete(modalType);
        }
    };

    const panaltyContent = () => {
        return (
            <>
                <p className="mb-0">{rentalConsentSummary.summaryNotice}</p>
                <div className="mt-3">
                    {rentalConsentSummary.sections.map((section, index) => (
                        <>
                            <Row
                                justify="space-between"
                                style={{
                                    backgroundColor: "#F5F5F5",
                                    borderTop: "1px solid #AEB3BC",
                                    borderBottom: "1px solid #DFE2E7",
                                    padding: "10px",
                                }}
                                className="mb-1"
                            >
                                <Col>
                                    <p style={{ margin: 0 }}>{`${index + 1}. ${section.title}`}</p>
                                </Col>
                                <Col>
                                    <Checkbox className="text-bold" checked={!!checkedMap[index]} onChange={(e) => toggleCheck(index, e.target.checked)}>
                                        동의합니다.<span className="text-danger">*</span>
                                    </Checkbox>
                                </Col>
                            </Row>
                            <ul>
                                {section.items.map(
                                    (item, index) =>
                                        item.text && (
                                            <li key={index}>
                                                <p style={{ margin: 0 }} className={item.highlight ? "text-danger" : ""}>
                                                    {item.text}
                                                </p>
                                                <ul style={{ listStyle: "none", paddingLeft: 0 }}>
                                                    {item.subItems?.map((subItem, subIdx) => (
                                                        <li key={subIdx}>- {subItem}</li>
                                                    ))}
                                                </ul>
                                            </li>
                                        )
                                )}
                            </ul>
                            <div style={{ maxHeight: "193px", overflowY: "auto" }} key={`content-${index}`}>
                                {section.items.map((item, index) => item.content && <>{item.content}</>)}
                            </div>
                        </>
                    ))}
                </div>
                <div style={{ borderTop: "1px solid #AEB3BC", borderBottom: "1px solid #AEB3BC" }}>
                    <ul className="mt-2 mb-2">
                        <li>위약금은 발생 시점에 따라 실비 기준으로 달라질 수 있습니다.</li>
                        <li>본인은 위 내용을 충분히 읽고 이해하였으며, 이에 동의합니다.</li>
                    </ul>
                </div>
            </>
        );
    };

    const videoContent = () => {
        return (
            <>
                <p className="mb-0">
                    출차 안내 영상은 스킵없이 끝까지 시청해야 사전 체크가 완료됩니다. <br />
                    렌트 이용일 전까지 사전 체크를 완료해 주세요. 사전 체크 미완료 시 차량 출차가 불가합니다.
                </p>
                <div className="mt-3">
                    {videoContents.map((content, index) => (
                        <div key={index}>
                            <Row
                                justify="space-between"
                                style={{
                                    backgroundColor: "#F5F5F5",
                                    borderTop: "1px solid #AEB3BC",
                                    borderBottom: "1px solid #DFE2E7",
                                    padding: "10px",
                                }}
                                className="mb-1"
                            >
                                <Col>
                                    <p style={{ margin: 0 }}>{`${index + 1}. ${content.category}`}</p>
                                </Col>
                                <Col>
                                    <Checkbox
                                        className="text-bold"
                                        disabled={!rent.checkedExitPolicy && !finished[index]}
                                        checked={!!checkedMap[index]}
                                        onChange={(e) => toggleCheck(index, e.target.checked)}
                                    >
                                        시청 완료<span className="text-danger">*</span>
                                    </Checkbox>
                                </Col>
                            </Row>
                            <VideoPlayer key={content.url} url={content.url} playerKey={playerKeys[index]} onVideoEnd={() => onVideoEnd(index)} />
                        </div>
                    ))}
                </div>
            </>
        );
    };

    return (
        <>
            <MobileView className="mobile">
                <div onClick={() => setIsOpen(true)} style={{ display: "inline-block" }}>
                    <div className="mb-2" style={{ position: "relative" }}>
                        <Space size={10} className="mb-0">
                            <p style={{ fontWeight: "700", color: !rent?.canceledDate ? "#005FDB" : "#E21D12" }}>{rent && Utils.convertRentType(rent)}</p>
                            <p>|</p>
                            <p style={{ color: "#192739F0" }}>{rent?.createdDate && Utils.utcToLocalTime(rent?.createdDate, Common.FORMAT_DATE_TIME)}</p>
                            {isShowErrorIcon() && <img className="mr-1 mb-1" src={IconError} height={16} width={16} />}
                        </Space>
                        <img src={IconRight} style={{ float: "right", width: "24px", height: "24px" }} />
                    </div>
                    <Title searchType={true} info={title} disabledPrice />
                </div>
                {!isLast && <Divider className="custom-divider" />}
                <MobileDrawer
                    isOpen={isOpen}
                    title={String.reservationInfo}
                    content={displayDetail()}
                    onClose={() => {
                        setIsOpen(false);
                    }}
                />
            </MobileView>

            <BrowserView className="browser">
                <div className="order-card mt-3">
                    <Collapse className="mb-3" expandIconPosition="end" onChange={(e) => e.length !== 0 && checkDriverLicenseOpenPopup()}>
                        <Panel
                            key={Number(rent?.rentId)}
                            header={
                                <Space direction="vertical" size="middle">
                                    <Space size={10}>
                                        <p style={{ fontWeight: "700", fontSize: 16, color: !rent?.canceledDate ? "#005FDB" : "#E21D12" }}>
                                            {rent && Utils.convertRentType(rent)}
                                        </p>
                                        <p>|</p>
                                        <p style={{ color: "#192739F0", fontSize: 16 }}>
                                            {getPayment()?.paidDate !== undefined &&
                                                Utils.utcToLocalTime(getPayment()?.paidDate as string, Common.FORMAT_DATE_TIME)}
                                        </p>
                                        {isShowErrorIcon() && <img className="mr-1 mb-1" src={IconError} height={16} width={16} />}
                                    </Space>
                                    <Row gutter={[20, 20]}>
                                        <Col>
                                            <img
                                                style={{ borderRadius: "8px", width: "200px", height: "106px" }}
                                                src={Utils.getCarImage(rent?.rentCar?.car?.carType?.typeName)}
                                            />
                                        </Col>
                                        <Col>
                                            <Title info={title} disabledPrice />
                                            <Space size="middle" style={{ marginBottom: "8px" }}>
                                                <Button
                                                    className="btn-light-border"
                                                    disabled={rent?.rentCar?.repairing || rent?.rentCar?.waiting || rent?.rentCar?.unavailable}
                                                    onClick={(e) => onClick("showDetail", e)}
                                                >
                                                    {String.showGoods}
                                                </Button>
                                                {rent && Utils.checkRentType(rent, [String.reservationComplete]) && (
                                                    <Button className="btn-danger" onClick={(e) => onClick("cancel", e)}>
                                                        {String.reservationCancel}
                                                    </Button>
                                                )}
                                            </Space>
                                        </Col>
                                    </Row>
                                </Space>
                            }
                        >
                            <>
                                <AdditionalDriverInfo
                                    rent={rent}
                                    disabled={isPast}
                                    onSuccess={(additionalDriver) => onComplete && onComplete(undefined, additionalDriver)}
                                />
                                {!isPast && (
                                    <>
                                        <Divider />
                                        <h6>사전체크</h6>
                                        <Row gutter={[20, 20]}>
                                            <Col xs={24} sm={24} md={12}>
                                                <Space
                                                    className="box"
                                                    style={{ width: "100%", display: "flex", justifyContent: "space-between", padding: "5px 14px" }}
                                                >
                                                    <p style={{ height: "100%" }}>위약금 및 이용 안내</p>
                                                    <Button
                                                        className={`${styles.btn} ${styles.borderLightBtn}`}
                                                        onClick={(e) => {
                                                            if (isThreeDaysAgo()) {
                                                                setModalType("checkedUsagePolicy");
                                                                setIsModalOpen(true);
                                                            } else {
                                                                openCheckErrorPopup();
                                                            }
                                                        }}
                                                    >
                                                        {rent?.checkedUsagePolicy ? "완료" : "이용 전 체크"}
                                                    </Button>
                                                </Space>
                                            </Col>
                                            <Col xs={24} sm={24} md={12}>
                                                <Space
                                                    className="box"
                                                    style={{ width: "100%", display: "flex", justifyContent: "space-between", padding: "5px 14px" }}
                                                >
                                                    <p style={{ height: "100%" }}>출차 안내</p>
                                                    <Button
                                                        className={`${styles.btn} ${styles.borderLightBtn}`}
                                                        onClick={(e) => {
                                                            if (isThreeDaysAgo()) {
                                                                setModalType("checkedExitPolicy");
                                                                setIsModalOpen(true);
                                                            } else {
                                                                openCheckErrorPopup();
                                                            }
                                                        }}
                                                    >
                                                        {rent?.checkedExitPolicy ? "완료" : "이용 전 체크"}
                                                    </Button>
                                                </Space>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                                <Divider />
                                <Row gutter={[20, 20]}>
                                    <Col xs={24} sm={24} md={12}>
                                        <h6>{String.reservationInfo}</h6>
                                        <Space direction="vertical" size="small" style={{ width: "100%" }}>
                                            <Space className="box" style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                                                <p>예약번호</p>
                                                <p>{rent?.rentNo}</p>
                                            </Space>
                                            <div className="box">
                                                <ReservationDateInfo
                                                    isB2b={!!rent?.rentCar?.b2b}
                                                    period={
                                                        moment(rent?.startDate).format(Common.FORMAT_DATE_WITH_DAY) +
                                                        " ~ " +
                                                        moment(rent?.endDate).format(Common.FORMAT_DATE_WITH_DAY)
                                                    }
                                                />
                                            </div>
                                        </Space>
                                    </Col>
                                    <Col xs={24} sm={24} md={12}>
                                        <h6>{rent && Utils.checkRentType(rent, [String.cancellationComplete]) ? String.refundInfo : String.paymentInfo}</h6>
                                        <Space direction="vertical" size="small" style={{ width: "100%" }}>
                                            <div className="box">
                                                <ReservationDetail
                                                    showTitle={false}
                                                    value={{
                                                        rentCar: rent?.rentCar,
                                                        regionId: rent?.rentCar?.rentRegion?.rentRegionId,
                                                        startDate: rent?.startDate,
                                                        endDate: rent?.endDate,
                                                        extraTime: rent?.rentExtraTime,
                                                        rentPaidItems: rent?.rentPaidItems,
                                                        insurance: rent?.rentInsurance,
                                                        coupon: rent?.userCoupon,
                                                        multiCoupon: rent?.userMultiCoupon,
                                                        cancelAmount: getPayment()?.paymentImp?.cancelAmount,
                                                    }}
                                                    paidInsurance={paidInsurance}
                                                    paidItems={paidItems?.map((item) => item.rentPaidItemId)}
                                                    rentPeakSeasons={rentPeakSeasons}
                                                />
                                            </div>
                                            <div className="box">
                                                <Space direction="vertical" size={0}>
                                                    <div style={{ display: "flex", alignItems: "center" }}>
                                                        <CalendarOutlined style={{ marginRight: "8px" }} />
                                                        {rent && Utils.checkRentType(rent, [String.cancellationComplete]) ? (
                                                            <p>
                                                                취소 일시&nbsp;:&nbsp;
                                                                {getPayment()?.canceledDate !== undefined &&
                                                                    Utils.utcToLocalTime(getPayment()?.canceledDate as string, Common.FORMAT_DATE_TIME)}
                                                            </p>
                                                        ) : (
                                                            <p>
                                                                결제 일시&nbsp;:&nbsp;
                                                                {getPayment()?.paidDate !== undefined &&
                                                                    Utils.utcToLocalTime(getPayment()?.paidDate as string, Common.FORMAT_DATE_TIME)}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div style={{ display: "flex", alignItems: "center" }}>
                                                        <CreditCardOutlined style={{ marginRight: "8px" }} />
                                                        <p>결제 수단&nbsp;:&nbsp;</p>
                                                        <p>{getPayment()?.paidDate ? getPaymentMethod() : String.dash}</p>
                                                    </div>
                                                </Space>
                                            </div>
                                        </Space>
                                    </Col>
                                </Row>
                                {!isPast && (
                                    <>
                                        <Divider />
                                        <Row gutter={[20, 20]}>
                                            <Col xs={24} sm={24} md={12}>
                                                <h6>이용 안내</h6>
                                                <div className="box">
                                                    <Info
                                                        showInfo={{ show: true, showDivider: true }}
                                                        showCondition={{ show: true, showTitle: true }}
                                                        isB2b={!!rent?.rentCar?.b2b}
                                                        carType={rent?.rentCar?.car?.carType?.typeName}
                                                    />
                                                </div>
                                            </Col>
                                            <Col xs={24} sm={24} md={12}>
                                                <h6>반납 장소</h6>
                                                <div className="box">
                                                    <Info rentRegion={rent?.rentCar?.rentRegion} showMap={{ show: true }} isB2b={!!rent?.rentCar?.b2b} />
                                                </div>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                            </>
                        </Panel>
                    </Collapse>
                    <RentModal
                        visible={visible}
                        title="예약을 취소하시면 환불규정에 따라 아래의 위약금이 부과됩니다."
                        subTitle={"계좌이체 항목은 별도 환불처리 됩니다.\n예약을 취소하시겠습니까?"}
                        content={
                            rent !== undefined && (
                                <>
                                    <Space className="mt-3" style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                                        <p className="b-md">차량 대여 금액</p>
                                        <NumericFormat
                                            className="b-md"
                                            value={getRentalPrice()}
                                            thousandSeparator={true}
                                            suffix={String.priceUnit}
                                            displayType="text"
                                        />
                                    </Space>
                                    <Space style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                                        <p className="text-danger b-md">{`위약금(${getCancelPercent(rent.startDate)}%)`}</p>
                                        <NumericFormat
                                            className="text-danger b-md"
                                            value={getCancelAmount(rent.startDate, getRentalPrice() || 0)}
                                            thousandSeparator={true}
                                            suffix={String.priceUnit}
                                            prefix="- "
                                            displayType="text"
                                        />
                                    </Space>
                                    {insurancePrice > 0 && (
                                        <Space className="mt-3" style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                                            <p className="b-md">자차 보험 {paidInsurance && "(계좌이체)"}</p>
                                            <NumericFormat
                                                className="b-md"
                                                value={insurancePrice}
                                                thousandSeparator={true}
                                                suffix={String.priceUnit}
                                                displayType="text"
                                            />
                                        </Space>
                                    )}
                                    {rent.rentPaidItems.length > 0 &&
                                        rent.rentPaidItems.map((item, i) => (
                                            <Space
                                                key={item.rentPaidItemId}
                                                className={`${i === 0 ? "mt-3" : ""}`}
                                                style={{ width: "100%", display: "flex", justifyContent: "space-between" }}
                                            >
                                                <p className="b-md">
                                                    {item.rentPaidItemName}
                                                    {paidItems?.find((paidItem) => paidItem.rentPaidItemId === item.rentPaidItemId) && "(계좌이체)"}
                                                </p>
                                                <NumericFormat
                                                    className="b-md"
                                                    value={
                                                        rent?.userCoupon?.coupon.rentPaidItemId !== item.rentPaidItemId &&
                                                        rent?.userMultiCoupon?.coupon.rentPaidItemId !== item.rentPaidItemId
                                                            ? item.price
                                                            : 0
                                                    }
                                                    thousandSeparator={true}
                                                    suffix={String.priceUnit}
                                                    displayType="text"
                                                />
                                            </Space>
                                        ))}
                                    <Divider />
                                    <Space style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                                        <p className="b-lg text-bold">총 환불 금액</p>
                                        <NumericFormat
                                            className="b-lg text-price text-bold"
                                            value={getTotalPrice()}
                                            thousandSeparator={true}
                                            suffix={String.priceUnit}
                                            displayType="text"
                                        />
                                    </Space>
                                </>
                            )
                        }
                        onOk={handleRentCancel}
                        onCancel={() => setVisible(false)}
                    />
                    <Modal
                        open={isModalOpen}
                        onCancel={() => {
                            setIsModalOpen(false);
                        }}
                        width="40vw"
                        footer={[
                            <Button
                                key="cancel"
                                className="btn-light-border"
                                style={{ width: "80px" }}
                                onClick={() => {
                                    setIsModalOpen(false);
                                }}
                            >
                                {String.cancel}
                            </Button>,
                            <Button
                                key="ok"
                                className="btn-dark"
                                type="primary"
                                style={{ width: "80px" }}
                                disabled={
                                    modalType === "checkedUsagePolicy"
                                        ? !rentalConsentSummary.sections.every((_, index) => checkedMap[index])
                                        : !videoContents.every((_, index) => checkedMap[index])
                                }
                                onClick={handleModalOk}
                            >
                                {String.confirm}
                            </Button>,
                        ]}
                        title={modalType === "checkedUsagePolicy" ? "위약금 및 이용 안내" : "출차 안내"}
                    >
                        <div style={{ maxHeight: "55vh", overflowY: "auto" }}>{modalType === "checkedUsagePolicy" ? panaltyContent() : videoContent()}</div>
                    </Modal>
                </div>
            </BrowserView>
        </>
    );
};

export default ReservationCard;
